import { useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import Tooltip from '@mui/material/Tooltip';
import { Box } from '@mui/material';
import { downloadExcelSheetFunction } from '../../common/functions/download_excel_sheet..function';

const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string') {
    const numericString = value.replace(/[^0-9.-]/g, '');
    if (!numericString) {
      return undefined;
    }
    const parsed = Number(numericString);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const formatDateValue = (value) => {
  if (!value) {
    return '--';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  return value;
};

const formatLengthValue = (value) => {
  const numeric = parseNumber(value);
  if (numeric === undefined) {
    return '--';
  }
  return `${numeric.toFixed(2)} KM`;
};

export default function NetworkLayerTable({ ranData = [], fiberData = [], inventoryData = [] }) {
  const [fiberTab, setFiberTab] = useState('RAN_DB');

  const data =
    fiberTab === 'RAN_DB' ? ranData : fiberTab === 'FIBER_SITE' ? fiberData : inventoryData;

  const cleanedData = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    if (fiberTab === 'RAN_DB') {
      return data.map((item) => ({
        site_code: item.site_code,
        region: item.region,
        physical: item.physical,
        site_status: item.site_status,
        locked_status: item.locked_status,
        status: item.status,
        coverage_type: item.coverage_type,
        commercial_city: item.commercial_city,
        pta_district: item.pta_district,
        site_type: item.site_type,
        mbu: item.mbu,
      }));
    } else if (fiberTab === 'FIBER_SITE') {
      return data.map((item) => ({
        site_id: item.site_id,
        region: item.region,
        city: item.city,
        phase_name: item.phase_name,
        bp: item.bp,
        ring_name: item.ring_name,
        fiber_pop_id: item.fiber_pop_id,
        status: item.status,
        total_length: item.total_length_mtr,
        on_net: item.on_net_mtr,
        off_net: item.offnet_mtr,
      }));
    } else {
      // FIBER_CABLE - inventory data
      return data.map((item) => {
        const fiberPair = parseNumber(item.fiber_pair ?? item.pair_count);
        const usedPair = parseNumber(item.used_pair);
        const availablePair =
          parseNumber(item.available_pair ?? item.unused_pair) ??
          (fiberPair !== undefined && usedPair !== undefined
            ? Math.max(fiberPair - usedPair, 0)
            : undefined);
        const lengthValue = parseNumber(
          item.length ?? item.link_length ?? item.link_length_km ?? item.distance_km
        );

        return {
          layer_name: item.layer_name ?? '--',
          fiber_type: item.fiber_type ?? '--',
          fiber_pair: fiberPair,
          used_pair: usedPair,
          available_pair: availablePair,
          link_rate_gbps: item.link_rate_gbps ?? item.link_rate ?? '--',
          fiber_tenancy_type: item.fiber_tenancy_type ?? item.tenancy_type ?? '--',
          commission_date: formatDateValue(item.commission_date ?? item.commissioned_at),
          scope_year: item.scope_year ?? item.scopeYear ?? '--',
          region: item.region ?? item.source_region ?? '--',
          city: item.city ?? item.source_city ?? '--',
          ring_unique_name: item.ring_unique_name ?? item.source_ring ?? '--',
          source_site_code: item.source_site_code ?? '--',
          sink_site_code: item.sink_site_code ?? '--',
          link_type: item.link_type ?? '--',
          vendor: item.vendor ?? '--',
          length: lengthValue,
        };
      });
    }
  }, [data, fiberTab]);

  const columns = useMemo(() => {
    const defaultCell = ({ cell }) => formatCellValue(cell.getValue());

    if (fiberTab === 'RAN_DB') {
      return [
        { accessorKey: 'site_code', header: 'Site Code' },
        { accessorKey: 'region', header: 'Region' },
        { accessorKey: 'physical', header: 'Technology' },
        { accessorKey: 'site_status', header: 'Site Status' },
        {
          accessorKey: 'locked_status',
          header: 'Locked Status',
          Cell: ({ cell }) => {
            const status = cell.getValue();
            let textColor = '';
            let bgColor = '';

            if (status === 'Temporary Locked') {
              textColor = '#2196F3';
              bgColor = '#E5F6FD';
            } else if (status === 'Inactive') {
              textColor = '#12A150';
              bgColor = '#E8FAF0';
            } else if (status === 'Locked') {
              textColor = '#BD1D23';
              bgColor = '#F8E8E9';
            }

            return (
              <Box
                sx={{
                  color: textColor,
                  backgroundColor: bgColor || 'transparent',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  display: 'inline-block',
                }}
              >
                {status || ''}
              </Box>
            );
          },
        },
        {
          accessorKey: 'status',
          header: 'Status',
          Cell: ({ cell }) => {
            const status = cell.getValue();
            let textColor = '';
            let bgColor = '';

            if (status === 'on_air') {
              textColor = '#2196F3';
              bgColor = '#E5F6FD';
            } else if (status === 'in_progress') {
              textColor = '#12A150';
              bgColor = '#E8FAF0';
            } else if (status === 'planned') {
              textColor = '#BD1D23';
              bgColor = '#F8E8E9';
            }

            return (
              <Box
                sx={{
                  color: textColor,
                  backgroundColor: bgColor || 'transparent',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  display: 'inline-block',
                }}
              >
                {status || ''}
              </Box>
            );
          },
        },
        { accessorKey: 'coverage_type', header: 'Coverage Type' },
        { accessorKey: 'commercial_city', header: 'City' },
        { accessorKey: 'pta_district', header: 'District' },
        { accessorKey: 'site_type', header: 'Site Type' },
        { accessorKey: 'mbu', header: 'MBU' },
      ];
    } else if (fiberTab === 'FIBER_SITE') {
      return [
        { accessorKey: 'site_id', header: 'Site ID' },
        { accessorKey: 'region', header: 'Region' },
        { accessorKey: 'city', header: 'City' },
        { accessorKey: 'phase_name', header: 'Phase' },
        {
          accessorKey: 'bp',
          header: 'Vendor',
          Cell: ({ cell }) => {
            const vendor = cell.getValue();
            let textColor = '';
            let bgColor = '';

            switch (vendor) {
              case 'Wateen':
                textColor = '#e6194b';
                bgColor = '#fdecea';
                break;
              case 'Nayatel':
                textColor = '#3cb44b';
                bgColor = '#e8f5e9';
                break;
              case 'PTCL':
                textColor = '#4363d8';
                bgColor = '#e8eaf6';
                break;
              case 'MPPL':
                textColor = '#ff9800';
                bgColor = '#fff3e0';
                break;
              case 'DKTCC':
                textColor = '#9c27b0';
                bgColor = '#f3e5f5';
                break;
              default:
                textColor = '#000';
                bgColor = 'transparent';
                break;
            }

            return (
              <Box
                sx={{
                  color: textColor,
                  backgroundColor: bgColor,
                  borderRadius: '4px',
                  padding: '2px 8px',
                  display: 'inline-block',
                }}
              >
                {vendor || ''}
              </Box>
            );
          },
        },
        { accessorKey: 'ring_name', header: 'Ring' },
        { accessorKey: 'fiber_pop_id', header: 'Fiber POP ID' },
        {
          accessorKey: 'status',
          header: 'Status',
          Cell: ({ cell }) => {
            const status = cell.getValue();
            let textColor = '';
            let bgColor = '';

            if (status === 'On-Air') {
              textColor = '#2196F3';
              bgColor = '#E5F6FD';
            } else if (status === 'In-Progress') {
              textColor = '#12A150';
              bgColor = '#E8FAF0';
            }

            return (
              <Box
                sx={{
                  color: textColor,
                  backgroundColor: bgColor || 'transparent',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  display: 'inline-block',
                }}
              >
                {status || ''}
              </Box>
            );
          },
        },
        { accessorKey: 'total_length', header: 'Total Length (m)' },
        { accessorKey: 'on_net', header: 'On-Net (m)' },
        { accessorKey: 'off_net', header: 'Off-Net (m)' },
      ];
    } else {
      // FIBER_CABLE columns
      return [
        { accessorKey: 'layer_name', header: 'Layer Name', size: 160, Cell: defaultCell },
        { accessorKey: 'fiber_type', header: 'Fiber Type', size: 120, Cell: defaultCell },
        { accessorKey: 'region', header: 'Region', size: 120, Cell: defaultCell },
        { accessorKey: 'city', header: 'City', size: 120, Cell: defaultCell },
        { accessorKey: 'ring_unique_name', header: 'Ring', size: 180, Cell: defaultCell },
        {
          accessorKey: 'fiber_tenancy_type',
          header: 'Tenancy Type',
          size: 160,
          Cell: defaultCell,
        },
        {
          accessorKey: 'vendor',
          header: 'Vendor',
          size: 120,
          Cell: ({ cell }) => {
            const value = cell.getValue();
            const displayValue = formatCellValue(value);

            const stylesByVendor = {
              JAZZ: {
                background: '#FEE2E2',
                color: '#B91C1C',
              },
              WATEEN: {
                background: '#DBEAFE',
                color: '#1D4ED8',
              },
              WTL: {
                background: '#FFF7ED',
                color: '#C2410C',
              },
              NAYATEL: {
                background: '#FEF3C7',
                color: '#B45309',
              },
              MPPL: {
                background: '#EDE9FE',
                color: '#6D28D9',
              },
              PTCL: {
                background: '#DCFCE7',
                color: '#047857',
              },
            };

            const defaultStyle = {
              background: '#F3F4F6',
              color: '#111827',
            };

            const vendorKey = String(value ?? '')
              .trim()
              .toUpperCase();
            const style = stylesByVendor[vendorKey] || defaultStyle;

            return (
              <span
                style={{
                  background: style.background,
                  color: style.color,
                  padding: '3px 10px',
                  fontSize: '10.5px',
                  fontWeight: '550',
                  borderRadius: '24px',
                  textTransform: 'capitalize',
                }}
              >
                {displayValue}
              </span>
            );
          },
        },
        { accessorKey: 'fiber_pair', header: 'Fiber Pair', size: 100, Cell: defaultCell },
        { accessorKey: 'used_pair', header: 'Used Pair', size: 100, Cell: defaultCell },
        { accessorKey: 'available_pair', header: 'Available Pair', size: 100, Cell: defaultCell },
        { accessorKey: 'link_rate_gbps', header: 'Link Rate (Gbps)', size: 100, Cell: defaultCell },
        {
          accessorKey: 'length',
          header: 'Length',
          size: 110,
          Cell: ({ cell }) => formatLengthValue(cell.getValue()),
        },
        { accessorKey: 'commission_date', header: 'Commission Date', size: 150, Cell: defaultCell },
        { accessorKey: 'scope_year', header: 'Scope Year', size: 120, Cell: defaultCell },
        { accessorKey: 'source_site_code', header: 'Source Site', size: 140, Cell: defaultCell },
        { accessorKey: 'sink_site_code', header: 'Sink Site', size: 140, Cell: defaultCell },
        { accessorKey: 'link_type', header: 'Link Type', size: 140, Cell: defaultCell },
      ].map((column) => ({
        ...column,
        muiTableHeadCellProps: {
          ...(column.muiTableHeadCellProps ?? {}),
          align: 'center',
          sx: {
            ...(column.muiTableHeadCellProps?.sx ?? {}),
            backgroundColor: '#E8E9E9',
            textAlign: 'center',
          },
        },
        muiTableBodyCellProps: {
          ...(column.muiTableBodyCellProps ?? {}),
          align: 'center',
          sx: {
            ...(column.muiTableBodyCellProps?.sx ?? {}),
            color: '#667085',
            fontSize: '12px',
            textAlign: 'center',
          },
        },
      }));
    }
  }, [fiberTab]);

  const table = useMaterialReactTable({
    columns,
    data: cleanedData,
    columnOrder: columns.map((col) => col.accessorKey),
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableRowSelection: false,
    enableColumnPinning: true,
    enableFacetedValues: true,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: '#BD1D23',
          color: 'white',
        },
      },
    },
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: true,
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 320px)' } },
    muiTableBodyCellProps: {
      sx: {
        color: '#667085',
        backgroundColor: '#fff',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '125%',
        height: '36px',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {/* Download Button */}
          <Tooltip title="Download">
            <svg
              style={{ cursor: 'pointer' }}
              onClick={downloadFile}
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                stroke="#0000008a"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7 10L12 15L17 10"
                stroke="#0000008a"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M12 15V3"
                stroke="#0000008a"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </Tooltip>
        </Box>
      </>
    ),

    muiTableBodyRowProps: ({ row }) => {
      const rawDateStr = row?.original?.lease_end_date_raw;

      if (!rawDateStr) {
        return {};
      }

      const leaseEndDate = new Date(rawDateStr);
      const now = new Date();
      const isExpired = leaseEndDate.getTime() < now.getTime();

      return isExpired
        ? {
            sx: {
              '& .MuiTableCell-root': {
                backgroundColor: '#FCA5A5 !important',
                color: '#111827 !important',
              },
            },
          }
        : {};
    },
  });

  const downloadFile = () => {
    const fileName =
      fiberTab === 'RAN_DB' ? 'ran_db' : fiberTab === 'FIBER_SITE' ? 'fiber_site' : 'fiber_cable';
    downloadExcelSheetFunction(cleanedData, fileName);
  };

  return (
    <Box sx={{ marginTop: '70px', width: '100%', height: '100%' }}>
      <Box
        sx={{
          backgroundColor: '#fff',
          border: '1px solid #E0E0E0',
          borderRadius: '8px',
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ padding: '8px 16px', backgroundColor: '#fff', marginRight: '16px' }}>
          {/* TABS SECTION ABOVE TABLE */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              borderBottom: '1px solid #ccc',
              width: '100%',
              backgroundColor: '#fff',
            }}
          >
            {['RAN DB', 'Fiber Site', 'Fiber Cable'].map((label) => {
              const key = label.toUpperCase().replace(/\s+/g, '_');
              const isSelected = fiberTab === key;

              return (
                <Box
                  key={key}
                  onClick={() => setFiberTab(key)}
                  sx={{
                    cursor: 'pointer',
                    px: 2,
                    py: 1,
                    borderBottom: isSelected ? '2px solid #BD1D23' : '2px solid transparent',
                    fontWeight: isSelected ? 700 : 400,
                    fontSize: isSelected ? '15px' : '14px',
                    color: '#000',
                  }}
                >
                  {label}
                </Box>
              );
            })}
          </Box>

          <div className="box" style={{ marginTop: '0px' }}>
            <MaterialReactTable table={table} />
          </div>
        </Box>
      </Box>
    </Box>
  );
}
